import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BadgeDollarSign,
  Building2,
  Eye,
  Filter,
  MapPin,
  Pencil,
  Save,
  Search,
  Settings,
  Smartphone,
  Store,
  Tag,
  Trash2,
  Undo2,
  User,
  Users,
  X,
} from 'lucide-react';
import Pagination from './Pagination';
import TableHeader from './TableHeader';
import TableZoomControls, { useTableZoom } from './TableZoomControls';
import FeeSettingsFields from './FeeSettingsFields';
import VenueEditFields from './VenueEditFields';
import { venueService } from '../services/api';
import apiService from '../services/apiService';
import { getFeeSourceLabel } from '../constants/feeSourceLabels';
import './FilterBar.css';
import './VenuesManagementPanel.css';

// NOTE: We intentionally cap the list for performance.
// TODO(future): migrate Venues list to server-side filtering + pagination (tenantId, feeSource, search) and remove the large upfront fetch.
const MAX_VENUES = 5000;

function formatFeeFixedEurFromCents(cents) {
  const value = Number(cents || 0);
  return (value / 100).toFixed(2);
}

const formatFeeSourceLabel = (source) => getFeeSourceLabel(source, '-');

const VenueColumnLabel = ({ icon: Icon, children }) => (
  <span className="venue-column-label">
    <Icon size={14} aria-hidden="true" />
    <span>{children}</span>
  </span>
);

const VenuesManagementPanel = ({
  lockedTenantId = '',
  showIntro = true,
  showTenantColumn = true,
  showTenantFilter = true
}) => {
  const navigate = useNavigate();
  const tableZoom = useTableZoom();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');

  // Filters state
  const [filters, setFilters] = useState({
    tenantId: lockedTenantId || '',
    feeSource: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting state
  const [sortColumn, setSortColumn] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Edit venue modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingVenue, setSavingVenue] = useState(false);
  const [venueCategories, setVenueCategories] = useState([]);
  const [pendingCategorySelection, setPendingCategorySelection] = useState({ hasPendingCategorySelection: false, categoryName: null });
  const [pendingCategoryModalOpen, setPendingCategoryModalOpen] = useState(false);
  const [pendingCategorySelectionResetKey, setPendingCategorySelectionResetKey] = useState(0);

  // Delete venue modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteVenue, setDeleteVenue] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleteError, setDeleteError] = useState('');

  // Fee modal
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [feeVenue, setFeeVenue] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeSaving, setFeeSaving] = useState(false);
  const [feeSource, setFeeSource] = useState('');
  const [feeForm, setFeeForm] = useState({
    soontipFeePercent: 0,
    soontipFeeFixedEur: '0.00'
  });
  const [feeSavedBanner, setFeeSavedBanner] = useState('');

  const loadVenues = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await venueService.getVenues({
        page: 1,
        limit: MAX_VENUES,
        ...(lockedTenantId ? { tenantId: lockedTenantId } : {})
      });
      const list = response?.venues || response?.data || response || [];
      setVenues(list);
    } catch (err) {
      console.error('Error loading venues:', err);
      setError('Failed to load venues');
    } finally {
      setLoading(false);
    }
  }, [lockedTenantId]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  useEffect(() => {
    apiService.getVenueCategories()
      .then((response) => setVenueCategories(response.data || []))
      .catch(() => setVenueCategories([]));
  }, []);

  useEffect(() => {
    // Keep tenant filter locked when used in tenant context
    if (!lockedTenantId) return;
    setFilters((prev) => ({ ...prev, tenantId: lockedTenantId }));
  }, [lockedTenantId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters]);

  const tenantOptions = useMemo(() => {
    const map = new Map();
    for (const v of venues) {
      const id = v?.tenant?.id;
      const name = v?.tenant?.company_name;
      if (id && name && !map.has(id)) {
        map.set(id, name);
      }
    }

    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [venues]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const filteredVenues = useMemo(() => {
    let filtered = venues;

    if (filters.tenantId) {
      filtered = filtered.filter(v => v?.tenant?.id === filters.tenantId);
    }

    if (filters.feeSource) {
      filtered = filtered.filter(v => (v?.feeSource || '') === filters.feeSource);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(v =>
        (v.name || '').toLowerCase().includes(query) ||
        (v.address || '').toLowerCase().includes(query) ||
        (v.tenant?.adminAlias || '').toLowerCase().includes(query) ||
        (v.tenant?.company_name || '').toLowerCase().includes(query)
      );
    }

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aVal;
        let bVal;

        switch (sortColumn) {
          case 'tenant':
            aVal = (a.tenant?.adminAlias || a.tenant?.company_name || '').toLowerCase();
            bVal = (b.tenant?.adminAlias || b.tenant?.company_name || '').toLowerCase();
            break;
          case 'name':
            aVal = (a.name || '').toLowerCase();
            bVal = (b.name || '').toLowerCase();
            break;
          case 'address':
            aVal = (a.address || '').toLowerCase();
            bVal = (b.address || '').toLowerCase();
            break;
          case 'teams':
            aVal = Number(a._count?.teams || 0);
            bVal = Number(b._count?.teams || 0);
            break;
          case 'workers':
            aVal = Number(a._count?.workers || 0);
            bVal = Number(b._count?.workers || 0);
            break;
          case 'devices':
            aVal = Number(a._count?.devices || 0);
            bVal = Number(b._count?.devices || 0);
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string') {
          const comparison = aVal.localeCompare(bVal);
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [venues, searchQuery, sortColumn, sortDirection, filters]);

  const paginatedVenues = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredVenues.slice(startIndex, endIndex);
  }, [filteredVenues, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const handleOpenEditModal = (venue) => {
    setSelectedVenue(venue);
    setEditFormData({
      name: venue.name || '',
      address: venue.address || ''
    });
    setPendingCategorySelection({ hasPendingCategorySelection: false, categoryName: null });
    setPendingCategoryModalOpen(false);
    setShowEditModal(true);
  };

  const handleVenueUpdated = (updatedVenue) => {
    if (!updatedVenue?.id) return;

    const category = venueCategories.find((item) => item.id === updatedVenue.venueCategoryId) || null;
    const normalizedVenue = {
      ...selectedVenue,
      ...updatedVenue,
      venueCategory: category
        ? { id: category.id, name: category.name, slug: category.slug }
        : null,
    };

    setSelectedVenue(normalizedVenue);
    setVenues((prev) => prev.map((venue) => (
      venue.id === updatedVenue.id
        ? {
            ...venue,
            ...updatedVenue,
            venueCategory: category
              ? { id: category.id, name: category.name, slug: category.slug }
              : null,
          }
        : venue
    )));
  };

  const performSaveVenue = async () => {
    if (!selectedVenue?.id) return;

    if (!editFormData.name?.trim() || !editFormData.address?.trim()) {
      alert('Nome e indirizzo sono obbligatori');
      return;
    }

    try {
      setSavingVenue(true);
      const response = await venueService.updateVenue(selectedVenue.id, {
        name: editFormData.name,
        address: editFormData.address,
      });

      const updatedVenue = response?.venue;

      if (updatedVenue) {
        // NB: the PUT /admin/venues/:id response doesn't include effective fee fields.
        // Merge to preserve `effectiveSoontipFeePercent` / `effectiveSoontipFeeFixedCents` already in the list.
        setVenues(prev => prev.map(v => (
          v.id === updatedVenue.id
            ? { ...v, ...updatedVenue }
            : v
        )));
      } else {
        await loadVenues();
      }

      setShowEditModal(false);
      setSelectedVenue(null);
    } catch (err) {
      console.error('Error saving venue:', err);
      alert(`Errore salvataggio venue: ${err.response?.data?.error || err.message}`);
    } finally {
      setSavingVenue(false);
    }
  };

  const handleSaveVenue = async () => {
    if (pendingCategorySelection.hasPendingCategorySelection) {
      setPendingCategoryModalOpen(true);
      return;
    }

    await performSaveVenue();
  };

  const handleDiscardPendingCategoryAndSave = async () => {
    setPendingCategoryModalOpen(false);
    setPendingCategorySelection({ hasPendingCategorySelection: false, categoryName: null });
    setPendingCategorySelectionResetKey((value) => value + 1);
    await performSaveVenue();
  };

  const handleGoToDetails = (venueId) => {
    if (!venueId) return;
    navigate(`/admin/venue/${venueId}`);
  };

  const handleOpenDeleteModal = async (venue) => {
    if (!venue?.id) return;

    setDeleteVenue(venue);
    setDeleteConfirmName('');
    setDeleteError('');
    setDeletePreview(null);
    setShowDeleteModal(true);
    setDeleteLoading(true);

    try {
      const preview = await venueService.getVenueDeletionPreview(venue.id);
      setDeletePreview(preview);
    } catch (err) {
      console.error('Error loading venue deletion preview:', err);
      setDeleteError(err.response?.data?.error || err.message || 'Errore caricamento anteprima eliminazione');
    } finally {
      setDeleteLoading(false);
    }
  };

  const deleteCountsRows = useMemo(() => {
    const counts = deletePreview?.deleteCounts;
    if (!counts) return [];

    const rows = [
      { key: 'teams', label: 'Teams' },
      { key: 'workers', label: 'Workers' },
      { key: 'devices', label: 'Devices' },
      { key: 'areas', label: 'Aree' },
      { key: 'shifts', label: 'Turni' },
      { key: 'shiftEvents', label: 'Eventi Turno' },
      { key: 'userVenueRoles', label: 'Ruoli (UserVenueRole)' },
      { key: 'venueInvites', label: 'Inviti Venue' },
      { key: 'teamInvites', label: 'Inviti Team' },
      { key: 'workerInvites', label: 'Inviti Worker' },
      { key: 'inviteConsentAcceptances', label: 'Consensi Inviti' },
      { key: 'tipPaymentIntents', label: 'Pagamenti (TipPaymentIntent)' },
      { key: 'tipTransfers', label: 'Trasferimenti (TipTransfer)' },
      { key: 'tipTransferFailureLogs', label: 'Transfer Failure Log' },
      { key: 'invoices', label: 'Fatture (Invoice)' },
      { key: 'deviceSessions', label: 'Sessioni Device' },
      { key: 'deviceEvents', label: 'Eventi Device' },
      { key: 'tokenBlacklist', label: 'Token Blacklist' },
      { key: 'deviceHealthStatuses', label: 'Health Status' },
      { key: 'deviceAppUpdateLogs', label: 'Update Logs' }
    ];

    return rows
      .map(r => ({ ...r, value: Number(counts?.[r.key] || 0) }))
      .filter(r => r.value > 0);
  }, [deletePreview]);

  const deleteNameMatches = useMemo(() => {
    const expected = String(deleteVenue?.name || '').trim();
    const typed = String(deleteConfirmName || '').trim();
    if (!expected) return false;
    return typed === expected;
  }, [deleteVenue, deleteConfirmName]);

  const handleConfirmDeleteVenue = async () => {
    if (!deleteVenue?.id) return;
    setDeleteError('');

    try {
      setDeleteDeleting(true);
      await venueService.deleteVenue(deleteVenue.id, {
        confirmName: deleteConfirmName
      });
      setVenues(prev => prev.filter(v => v.id !== deleteVenue.id));
      setShowDeleteModal(false);
      setDeleteVenue(null);
      setDeletePreview(null);
      setDeleteConfirmName('');
    } catch (err) {
      console.error('Error deleting venue:', err);
      const apiError = err.response?.data;
      setDeleteError(apiError?.error || err.message || 'Errore eliminazione');
    } finally {
      setDeleteDeleting(false);
    }
  };

  const handleOpenFeeModal = async (venue) => {
    setFeeSavedBanner('');
    setFeeVenue(venue);
    setShowFeeModal(true);
    setFeeLoading(true);

    try {
      const response = await venueService.getVenueAdminSettings(venue.id);
      setFeeSource(response?.source || '');

      const percent = Number(response?.soontipFeePercent || 0);
      const fixedCents = Number(response?.soontipFeeFixedCents || 0);

      setFeeForm({
        soontipFeePercent: Number.isFinite(percent) ? percent : 0,
        soontipFeeFixedEur: formatFeeFixedEurFromCents(fixedCents)
      });
    } catch (err) {
      console.error('Error loading venue fee settings:', err);
      setFeeSource('');
      setFeeForm({ soontipFeePercent: 0, soontipFeeFixedEur: '0.00' });
    } finally {
      setFeeLoading(false);
    }
  };

  const handleSaveFee = async () => {
    if (!feeVenue?.id) return;

    const percent = Number(feeForm.soontipFeePercent);
    const fixed = Number(feeForm.soontipFeeFixedEur);

    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      alert('Percentuale non valida (0-100)');
      return;
    }

    if (!Number.isFinite(fixed) || fixed < 0) {
      alert('Fee fissa non valida');
      return;
    }

    try {
      setFeeSaving(true);
      await venueService.updateVenueAdminSettings(feeVenue.id, {
        soontipFeePercent: percent,
        soontipFeeFixedEur: fixed
      });

      const refreshed = await venueService.getVenueAdminSettings(feeVenue.id);
      setFeeSource(refreshed?.source || '');

      // Update list row immediately (effective values)
      setVenues(prev => prev.map(v => {
        if (v.id !== feeVenue.id) return v;
        return {
          ...v,
          feeSource: refreshed?.source || v.feeSource,
          effectiveSoontipFeePercent: refreshed?.soontipFeePercent,
          effectiveSoontipFeeFixedCents: refreshed?.soontipFeeFixedCents
        };
      }));

      // Close modal on save (requested UX)
      setShowFeeModal(false);
      setFeeVenue(null);
      setFeeSavedBanner('');
    } catch (err) {
      console.error('Error saving venue fee settings:', err);
      alert(`Errore salvataggio fee: ${err.response?.data?.error || err.message}`);
    } finally {
      setFeeSaving(false);
    }
  };

  const getDefaultFeeLabel = (venue) => {
    const contract = venue?.tenant?.contractOffers?.[0];
    if (contract && Number.isFinite(contract.feePercent)) {
      return `Contratto legacy (${contract.feePercent}%)`;
    }
    // new plan-based system: fee comes from planContractOffer snapshot or planVersion
    const sub = venue?.tenant?.subscriptions?.[0];
    if (sub?.planContractOffer?.feePercentSnapshot != null) {
      return `Offerta commerciale (${sub.planContractOffer.feePercentSnapshot}%)`;
    }
    if (sub?.planVersion?.feePercent != null) {
      return `Piano base (${sub.planVersion.feePercent}%)`;
    }
    return 'Global settings';
  };

  const handleResetFeeToDefault = async () => {
    if (!feeVenue?.id) return;

    const defaultLabel = getDefaultFeeLabel(feeVenue);
    const confirmed = window.confirm(
      `Vuoi ripristinare la fee di default?\n\nQuesto rimuove l'override del locale. La fee tornerà a: ${defaultLabel}.`
    );
    if (!confirmed) return;

    try {
      setFeeSaving(true);
      const reset = await venueService.resetVenueAdminSettings(feeVenue.id);

      setFeeSource(reset?.source || '');
      const percent = Number(reset?.soontipFeePercent || 0);
      const fixedCents = Number(reset?.soontipFeeFixedCents || 0);
      setFeeForm({
        soontipFeePercent: Number.isFinite(percent) ? percent : 0,
        soontipFeeFixedEur: formatFeeFixedEurFromCents(fixedCents)
      });

      setVenues(prev => prev.map(v => {
        if (v.id !== feeVenue.id) return v;
        return {
          ...v,
          feeSource: reset?.source || v.feeSource,
          effectiveSoontipFeePercent: reset?.soontipFeePercent,
          effectiveSoontipFeeFixedCents: reset?.soontipFeeFixedCents
        };
      }));

      setShowFeeModal(false);
      setFeeVenue(null);
      setFeeSavedBanner('');
    } catch (err) {
      console.error('Error resetting venue fee settings:', err);
      alert(`Errore ripristino fee: ${err.response?.data?.error || err.message}`);
    } finally {
      setFeeSaving(false);
    }
  };

  const formatFeePercent = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return `${num}%`;
  };

  const formatFeeFixed = (cents) => {
    const num = Number(cents);
    if (!Number.isFinite(num)) return null;
    return `${(num / 100).toFixed(2)}€`;
  };

  const formatFeeSummary = (venue) => {
    const percent = formatFeePercent(venue?.effectiveSoontipFeePercent);
    const fixed = formatFeeFixed(venue?.effectiveSoontipFeeFixedCents);

    if (percent && fixed) return `${percent} + ${fixed}`;
    if (percent) return percent;
    if (fixed) return fixed;
    return '-';
  };

  const columns = useMemo(() => {
    const cols = [];

    if (showTenantColumn) {
      cols.push({ key: 'tenant', label: <VenueColumnLabel icon={Building2}>Alias/Tenant</VenueColumnLabel>, sortable: true, width: '160px' });
    }

    cols.push(
      { key: 'name', label: <VenueColumnLabel icon={Store}>Locale</VenueColumnLabel>, sortable: true, width: '140px' },
      { key: 'address', label: <VenueColumnLabel icon={MapPin}>Indirizzo</VenueColumnLabel>, sortable: true, width: '240px' },
      { key: 'category', label: <VenueColumnLabel icon={Tag}>Categoria</VenueColumnLabel>, sortable: false, width: '150px' },
      { key: 'fee', label: <VenueColumnLabel icon={BadgeDollarSign}>Fee</VenueColumnLabel>, sortable: false, width: '110px' },
      { key: 'teams', label: <VenueColumnLabel icon={Users}>Teams</VenueColumnLabel>, sortable: true, width: '70px' },
      { key: 'workers', label: <VenueColumnLabel icon={User}>Workers</VenueColumnLabel>, sortable: true, width: '80px' },
      { key: 'devices', label: <VenueColumnLabel icon={Smartphone}>Devices</VenueColumnLabel>, sortable: true, width: '80px' },
      { key: 'actions', label: <VenueColumnLabel icon={Settings}>Azioni</VenueColumnLabel>, sortable: false, width: '170px' }
    );

    return cols;
  }, [showTenantColumn]);

  return (
    <div className="venues-page">
      {showIntro && (
        <header className="venues-page-header">
          <span className="venues-page-header-icon">
            <Store size={21} aria-hidden="true" />
          </span>
          <div>
            <h1>Gestione venues</h1>
            <p>Visualizza e gestisci locali, commissioni e struttura operativa.</p>
            <small>Visualizzazione limitata a {MAX_VENUES} venues.</small>
            {error && <div className="error">{error}</div>}
          </div>
        </header>
      )}

      <VenueFilterBar
        tenantOptions={tenantOptions}
        onFilterChange={(newFilters) => {
          // Always keep tenantId locked when used under tenant page
          const merged = lockedTenantId ? { ...newFilters, tenantId: lockedTenantId } : newFilters;
          setFilters(merged);
        }}
        totalCount={venues.length}
        filteredCount={filteredVenues.length}
        showTenantFilter={showTenantFilter && !lockedTenantId}
        lockedTenantId={lockedTenantId}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* Table */}
      <div className="card table-scroll-card">
        <TableZoomControls {...tableZoom} />
        <div className="table-container table-scroll-frame">
          <table
            className="admin-table venues-table mobile-zoom-table"
            style={{ '--mobile-table-zoom': `${tableZoom.zoom}%` }}
          >
            <TableHeader
              columns={columns}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
            <tbody>
              {paginatedVenues.map(v => (
                <tr key={v.id}>
                  {showTenantColumn && (
                    <td style={{ width: '160px', whiteSpace: 'normal', fontSize: '13px' }}>
                      {v.tenant?.adminAlias ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ color: '#495057', fontStyle: 'italic', fontWeight: 700, fontSize: '12px' }}>{v.tenant.adminAlias}</span>
                          <strong>{v.tenant?.company_name || 'N/A'}</strong>
                        </div>
                      ) : (
                        <strong>{v.tenant?.company_name || 'N/A'}</strong>
                      )}
                    </td>
                  )}
                  <td style={{ width: '140px', whiteSpace: 'normal', fontSize: '13px' }}>
                    <strong>{v.name}</strong>
                  </td>
                  <td style={{ width: '240px', whiteSpace: 'normal', fontSize: '12px', color: '#333' }}>
                    {v.address || 'N/A'}
                  </td>
                  <td style={{ width: '150px', whiteSpace: 'normal', fontSize: '12px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
                      <span
                        style={{
                          background: v.venueCategory?.name ? '#e0e7ff' : '#fff7ed',
                          color: v.venueCategory?.name ? '#3730a3' : '#c2410c',
                          padding: '3px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {v.venueCategory?.name || 'Nessuna categoria'}
                      </span>
                    </div>
                  </td>
                  <td style={{ width: '110px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', color: '#666', fontWeight: 600 }}>
                        {formatFeeSourceLabel(v.feeSource)}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#111' }}>
                        {formatFeeSummary(v)}
                      </span>
                    </div>
                  </td>
                  <td style={{ width: '70px', textAlign: 'center' }}>
                    {v._count?.teams ?? 0}
                  </td>
                  <td style={{ width: '80px', textAlign: 'center' }}>
                    {v._count?.workers ?? 0}
                  </td>
                  <td style={{ width: '80px', textAlign: 'center' }}>
                    {v._count?.devices ?? 0}
                  </td>
                  <td style={{ width: '120px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button
                        className="btn btn-mini btn-secondary"
                        onClick={() => handleOpenEditModal(v)}
                        title="Modifica informazioni locale"
                      >
                        <Pencil size={14} aria-hidden="true" />
                      </button>
                      <button
                        className="btn btn-mini"
                        style={{ backgroundColor: '#6f42c1', color: 'white' }}
                        onClick={() => handleGoToDetails(v.id)}
                        title="Apri dettagli locale"
                      >
                        <Eye size={14} aria-hidden="true" />
                      </button>
                      <button
                        className="btn btn-mini"
                        style={{ backgroundColor: '#17a2b8', color: 'white' }}
                        onClick={() => handleOpenFeeModal(v)}
                        title="Modifica commissione SoonTip"
                      >
                        <BadgeDollarSign size={14} aria-hidden="true" />
                      </button>
                      <button
                        className="btn btn-mini btn-danger"
                        onClick={() => handleOpenDeleteModal(v)}
                        title="Elimina locale"
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && (
          <p style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
            Caricamento venues...
          </p>
        )}

        {!loading && filteredVenues.length === 0 && (
          <p style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            Nessun locale trovato con i filtri selezionati.
          </p>
        )}

        {!loading && filteredVenues.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredVenues.length / itemsPerPage)}
            totalItems={filteredVenues.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Modal: Edit Venue */}
      {showEditModal && selectedVenue && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3><span className="venue-inline-label"><Pencil size={18} aria-hidden="true" /> Modifica locale</span></h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body">
              <VenueEditFields
                formData={editFormData}
                onChange={(field, value) => setEditFormData(prev => ({ ...prev, [field]: value }))}
                currentCategoryId={selectedVenue?.venueCategory?.id || selectedVenue?.venueCategoryId || null}
                currentCategoryName={selectedVenue?.venueCategory?.name || null}
                tenantName={selectedVenue?.tenant?.company_name || 'N/A'}
                venueId={selectedVenue?.id || null}
                venueName={selectedVenue?.name || null}
                categories={venueCategories}
                onVenueUpdated={handleVenueUpdated}
                onPendingCategorySelectionChange={setPendingCategorySelection}
                pendingCategorySelectionResetKey={pendingCategorySelectionResetKey}
              />

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={savingVenue}
                >
                  <span className="venue-inline-label"><X size={15} aria-hidden="true" /> Annulla</span>
                </button>
                <button
                  className="btn"
                  onClick={handleSaveVenue}
                  disabled={savingVenue}
                  style={{ backgroundColor: '#28a745', color: 'white' }}
                >
                  {savingVenue
                    ? 'Salvataggio...'
                    : <span className="venue-inline-label"><Save size={15} aria-hidden="true" /> Salva modifiche</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && pendingCategoryModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px' }}>
            <h3>Selezione categoria non confermata</h3>
            <div style={{ color: '#475569', lineHeight: 1.7 }}>
              Hai selezionato la categoria {pendingCategorySelection.categoryName ? <strong>“{pendingCategorySelection.categoryName}”</strong> : <strong>indicata</strong>} ma non hai cliccato <strong>Associa categoria</strong>.
              <div style={{ marginTop: 10 }}>
                Se vuoi salvare solo le altre modifiche del locale, puoi <strong>scartare la selezione categoria</strong>. Altrimenti torna al form e conferma la categoria con l'azione dedicata.
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={() => setPendingCategoryModalOpen(false)}>
                Continua a modificare
              </button>
              <button className="btn" onClick={handleDiscardPendingCategoryAndSave} style={{ backgroundColor: '#ea580c', color: 'white', borderColor: '#ea580c' }}>
                Scarta selezione e salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Fee */}
      {showFeeModal && feeVenue && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '650px' }}>
            <div className="modal-header">
              <h3><span className="venue-inline-label"><BadgeDollarSign size={18} aria-hidden="true" /> Commissione SoonTip</span></h3>
              <button
                className="modal-close"
                onClick={() => setShowFeeModal(false)}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '10px', color: '#666', fontSize: '13px' }}>
                <strong>{feeVenue.name}</strong> — {feeVenue.tenant?.company_name || 'N/A'}
              </div>

              {feeSavedBanner && (
                <div style={{
                  padding: '10px 12px',
                  background: '#e6ffed',
                  border: '1px solid #b7eb8f',
                  borderRadius: '6px',
                  marginBottom: '14px'
                }}>
                  {feeSavedBanner}
                </div>
              )}

              <div style={{ marginBottom: '12px', fontSize: '13px', color: '#444' }}>
                Sorgente attuale: <strong>{feeSource ? formatFeeSourceLabel(feeSource) : 'N/A'}</strong>
              </div>

              {feeLoading ? (
                <p style={{ color: '#666' }}>Caricamento impostazioni fee...</p>
              ) : (
                <>
                  <div className="form-group">
                    <FeeSettingsFields
                      percent={feeForm.soontipFeePercent}
                      onPercentChange={(value) => setFeeForm(prev => ({ ...prev, soontipFeePercent: value }))}
                      fixedEur={feeForm.soontipFeeFixedEur}
                      onFixedEurChange={(value) => setFeeForm(prev => ({ ...prev, soontipFeeFixedEur: value }))}
                      boldLabels={true}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
                    {feeSource === 'venue' && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '9px 18px', fontSize: '14px' }}
                          onClick={handleResetFeeToDefault}
                          disabled={feeSaving}
                        >
                          <span className="venue-inline-label"><Undo2 size={15} aria-hidden="true" /> Ripristina fee default</span>
                        </button>
                        <span style={{ fontSize: '11px', color: '#888' }}>
                          Il locale tornerà a usare: <strong>{getDefaultFeeLabel(feeVenue)}</strong>
                        </span>
                      </div>
                    )}
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '9px 18px', fontSize: '14px' }}
                      onClick={() => setShowFeeModal(false)}
                      disabled={feeSaving}
                    >
                      <span className="venue-inline-label"><X size={15} aria-hidden="true" /> Chiudi</span>
                    </button>
                    <button
                      className="btn"
                      style={{ padding: '9px 18px', fontSize: '14px', backgroundColor: '#28a745', color: 'white' }}
                      onClick={handleSaveFee}
                      disabled={feeSaving}
                    >
                      {feeSaving ? 'Salvataggio...' : 'Salva'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Delete Venue */}
      {showDeleteModal && deleteVenue && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '650px' }}>
            <div className="modal-header">
              <h3><span className="venue-inline-label"><Trash2 size={18} aria-hidden="true" /> Elimina locale</span></h3>
              <button
                className="modal-close"
                onClick={() => {
                  if (deleteDeleting) return;
                  setShowDeleteModal(false);
                  setDeleteVenue(null);
                  setDeletePreview(null);
                  setDeleteConfirmName('');
                  setDeleteError('');
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer'
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <div className="modal-body">
              <div style={{ marginBottom: '10px', color: '#444' }}>
                Stai per eliminare definitivamente il locale <strong>{deleteVenue?.name || '-'}</strong>.
              </div>

              {deleteLoading && (
                <p style={{ color: '#666' }}>Caricamento anteprima eliminazione...</p>
              )}

              {!deleteLoading && deletePreview && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, marginBottom: '8px' }}>Verrà eliminato:</div>

                  {deleteCountsRows.length === 0 ? (
                    <div style={{ color: '#666', fontSize: '13px' }}>
                      Nessun elemento associato trovato (verrà eliminato solo il locale).
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '6px 12px', fontSize: '13px' }}>
                      {deleteCountsRows.map((row) => (
                        <div key={row.key} style={{ display: 'contents' }}>
                          <div style={{ color: '#444' }}>{row.label}</div>
                          <div style={{ fontWeight: 700 }}>{row.value}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                    Utenti coinvolti: {Number(deletePreview?.affectedUsers?.totalDistinctUsers || 0)} (non vengono eliminati)
                  </div>
                </div>
              )}

              {deleteError && (
                <div className="error" style={{ marginBottom: '10px' }}>{deleteError}</div>
              )}

              <div className="form-group">
                <label style={{ fontWeight: 700 }}>
                  Per confermare, digita esattamente il nome del locale:
                </label>
                <input
                  type="text"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={deleteVenue?.name || ''}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    if (deleteDeleting) return;
                    setShowDeleteModal(false);
                    setDeleteVenue(null);
                    setDeletePreview(null);
                    setDeleteConfirmName('');
                    setDeleteError('');
                  }}
                  disabled={deleteDeleting}
                >
                  <span className="venue-inline-label"><X size={15} aria-hidden="true" /> Annulla</span>
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteVenue}
                  disabled={!deleteNameMatches || deleteDeleting || deleteLoading}
                  title={!deleteNameMatches ? 'Digita il nome del locale per abilitare eliminazione' : ''}
                >
                  {deleteDeleting ? 'Eliminazione...' : 'Elimina'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default VenuesManagementPanel;

// Venue FilterBar Component (Devices-like)
const VenueFilterBar = ({
  tenantOptions,
  onFilterChange,
  totalCount,
  filteredCount,
  showTenantFilter,
  lockedTenantId,
  searchQuery,
  onSearchChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState({
    tenantId: lockedTenantId || '',
    feeSource: ''
  });

  useEffect(() => {
    if (!lockedTenantId) return;
    setLocalFilters(prev => ({ ...prev, tenantId: lockedTenantId }));
  }, [lockedTenantId]);

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const empty = {
      tenantId: lockedTenantId || '',
      feeSource: ''
    };
    setLocalFilters(empty);
    onFilterChange(empty);
  };

  const activeCount = useMemo(() => {
    const values = [];
    if (showTenantFilter) values.push(localFilters.tenantId);
    values.push(localFilters.feeSource);
    return values.filter(v => v !== '').length;
  }, [localFilters, showTenantFilter]);

  const hasActiveFilters = activeCount > 0 || searchQuery.trim() !== '';

  const clearAllFilters = () => {
    clearFilters();
    onSearchChange('');
  };

  return (
    <div className="filter-bar">
      <div className="filter-toolbar">
        <div className="filter-search">
          <Search size={17} aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Cerca locale, indirizzo, alias o tenant..."
            aria-label="Cerca venue"
          />
          {searchQuery && (
            <button
              type="button"
              className="filter-search-clear"
              onClick={() => onSearchChange('')}
              title="Cancella ricerca"
              aria-label="Cancella ricerca"
            >
              <X size={15} />
            </button>
          )}
        </div>

        <button
          type="button"
          className="filter-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          <Filter size={16} aria-hidden="true" />
          <span>Filtri</span>
          {activeCount > 0 && (
            <span className="filter-badge">{activeCount}</span>
          )}
        </button>

        <div className="filter-results">
          <span className="result-count">
            {filteredCount} {filteredCount === 1 ? 'risultato' : 'risultati'}
            {hasActiveFilters && ` di ${totalCount}`}
          </span>
          {hasActiveFilters && (
            <button
              type="button"
              className="clear-filters"
              onClick={clearAllFilters}
            >
              <X size={14} />
              Rimuovi filtri
            </button>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="filter-controls">
          {showTenantFilter && (
            <div className="filter-group">
              <label>Tenant</label>
              <select
                value={localFilters.tenantId}
                onChange={(e) => handleFilterChange('tenantId', e.target.value)}
              >
                <option value="">Tutti</option>
                {tenantOptions.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Sorgente fee</label>
            <select
              value={localFilters.feeSource}
              onChange={(e) => handleFilterChange('feeSource', e.target.value)}
            >
              <option value="">Tutte</option>
              <option value="venue">Venue custom</option>
              <option value="global">Global</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};
